/**
 * Customer Slice
 * Manages customer state and active customer selection
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../services/api';

// R-52 v3: hydrate the last active customer synchronously from localStorage on
// module load. This closes the three-state gap that v2 (customerLoading flag)
// left open: on hard load / deep link, ManualRoute renders BEFORE Layout's
// fetchCustomers effect fires — so customerLoading is false, activeCustomer is
// null, and the capability check falls back to trackingMode='beacon' via D-1.
// By hydrating activeCustomer synchronously from cache, ManualRoute has a real
// customer object at render #1 and its capability check is meaningful from the
// first frame.
//
// The cached customer is OVERWRITTEN by fetchCustomers.fulfilled seconds later,
// so staleness is bounded by one round-trip. The alternative approaches (a
// hasAttempted flag; kicking off the fetch from ManualRoute; reordering the
// mount tree) all have the same or worse staleness properties.
//
// If localStorage is empty or the value is malformed, initial state is null and
// ManualRoute redirects to '/' — correct behaviour for a user with no cached
// customer.
const LAST_ACTIVE_CUSTOMER_KEY = 'lastActiveCustomer';

const hydrateActiveCustomer = () => {
  try {
    const raw = localStorage.getItem(LAST_ACTIVE_CUSTOMER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Sanity: only accept an object with at least an id. Anything else is
    // stale schema and we let the fetch replace it.
    return parsed && typeof parsed === 'object' && parsed.id ? parsed : null;
  } catch {
    return null;
  }
};

const initialState = {
  customerList: [],
  activeCustomer: hydrateActiveCustomer(),
  loading: false,
  error: null,
};

// Async thunks
export const fetchCustomers = createAsyncThunk(
  'customer/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      // R-11 (ASS-20): the store carries every customer the API returns,
      // regardless of status. Filtering by status happens at the READ site
      // that needs it (CustomerSelector shows only switchable customers;
      // admin CustomersPage shows all so Inactive/Pending can be edited).
      // Prior behaviour: this thunk hard-filtered to Active-only, which
      // hid Inactive customers from admin — breaking R-11's guard that
      // disabled customers 'remain visible and editable in customer
      // administration.'
      const result = await api.listCustomers();
      return result.items || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCustomer = createAsyncThunk(
  'customer/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const result = await api.getCustomer(id);
      return result.item;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createCustomerAction = createAsyncThunk(
  'customer/create',
  async (data, { rejectWithValue }) => {
    try {
      const result = await api.createCustomer(data);
      return result.item;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateCustomerAction = createAsyncThunk(
  'customer/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const result = await api.updateCustomer(id, data);
      return result.item;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const customerSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    setCustomers: (state, action) => {
      state.customerList = action.payload;
    },
    
    setActiveCustomer: (state, action) => {
      state.activeCustomer = action.payload;
      // Persist to localStorage.
      // - 'lastCustomer' (id only) is kept for backwards compatibility with the
      //   existing auto-select path in fetchCustomers.fulfilled.
      // - 'lastActiveCustomer' (full object) is R-52 v3: the module-scope
      //   hydrateActiveCustomer reads this key on load so ManualRoute has a
      //   real customer at render #1 on hard-load / deep-link.
      if (action.payload?.id) {
        localStorage.setItem('lastCustomer', action.payload.id);
        try {
          localStorage.setItem(LAST_ACTIVE_CUSTOMER_KEY, JSON.stringify(action.payload));
        } catch {
          // Quota exceeded or serialisation failure — non-fatal, the id key
          // above is still written and the fetch will re-resolve on next load.
        }
      }
    },
    
    clearActiveCustomer: (state) => {
      state.activeCustomer = null;
      // R-52 v3: also wipe the cached copy so a subsequent hard-load doesn't
      // resurrect the cleared customer.
      try {
        localStorage.removeItem(LAST_ACTIVE_CUSTOMER_KEY);
      } catch { /* non-fatal */ }
    },

    updateCustomerInList: (state, action) => {
      const customer = action.payload;
      state.customerList = state.customerList.map(c =>
        c.id === customer.id ? customer : c
      );
      if (state.activeCustomer?.id === customer.id) {
        state.activeCustomer = customer;
        // R-52 v3: refresh the cached copy so the hydrate on next hard-load
        // reads the current state, not the pre-edit one.
        try {
          localStorage.setItem(LAST_ACTIVE_CUSTOMER_KEY, JSON.stringify(customer));
        } catch { /* non-fatal */ }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.customerList = action.payload;

        // Auto-select customer if none selected. R-11 (ASS-20): the store now
        // carries Inactive customers too; the auto-select must NOT land on
        // one — the switcher hides them, so an auto-selected Inactive customer
        // would be unswitchable and stuck. Prefer the last selected id when
        // valid; otherwise the first non-Inactive customer; fall back to the
        // first customer only if the list is entirely Inactive (which is a
        // degenerate state and the same behaviour as before).
        if (!state.activeCustomer && action.payload.length > 0) {
          const isInactive = (c) => c && (c.status === 'Inactive' || c.status === 'inactive');
          const lastCustomerId = localStorage.getItem('lastCustomer');
          const lastCustomer = action.payload.find(c => c.id === lastCustomerId);
          if (lastCustomer && !isInactive(lastCustomer)) {
            state.activeCustomer = lastCustomer;
          } else {
            state.activeCustomer = action.payload.find(c => !isInactive(c)) || action.payload[0];
          }
          // R-52 v3: mirror the auto-selected customer into the cache so a
          // subsequent hard-load hydrates the current pick, not a stale one.
          if (state.activeCustomer?.id) {
            try {
              localStorage.setItem(LAST_ACTIVE_CUSTOMER_KEY, JSON.stringify(state.activeCustomer));
              localStorage.setItem('lastCustomer', state.activeCustomer.id);
            } catch { /* non-fatal */ }
          }
        }

        // R-52 v3: if a cached activeCustomer was hydrated at module load, the
        // fetch may have delivered a fresher copy of that same customer —
        // refresh the store and the cache from the fetched payload so any
        // server-side edits since last session are picked up.
        if (state.activeCustomer?.id) {
          const fresh = action.payload.find(c => c.id === state.activeCustomer.id);
          if (fresh) {
            state.activeCustomer = fresh;
            try {
              localStorage.setItem(LAST_ACTIVE_CUSTOMER_KEY, JSON.stringify(fresh));
            } catch { /* non-fatal */ }
          }
        }
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create
      .addCase(createCustomerAction.fulfilled, (state, action) => {
        state.customerList = [...state.customerList, action.payload];
      })
      
      // Update
      .addCase(updateCustomerAction.fulfilled, (state, action) => {
        const customer = action.payload;
        state.customerList = state.customerList.map(c => 
          c.id === customer.id ? customer : c
        );
        if (state.activeCustomer?.id === customer.id) {
          state.activeCustomer = customer;
        }
      });
  },
});

export const {
  setCustomers,
  setActiveCustomer,
  clearActiveCustomer,
  updateCustomerInList,
} = customerSlice.actions;

export default customerSlice.reducer;
